import React, { useEffect, useState, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getXperiences, Xperience, getXperienceTypes, LookupItem } from '../services/api';
import Navbar from '../components/Navbar';
import Button from '../components/Button';
import { t } from '../i18n';
import './HomePage.css';

const HOME_DASHBOARD_STATE_KEY = 'creator_dashboard_state_v1';

const getStoredHomeDashboardState = () => {
  try {
    const raw = sessionStorage.getItem(HOME_DASHBOARD_STATE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch {
    return null;
  }
};

const HomePage: React.FC = () => {
  const { creator, isProfileComplete } = useAuth();
  const navigate = useNavigate();
  const storedState = getStoredHomeDashboardState();
  const [xperiences, setXperiences] = useState<Xperience[]>([]);
  const [types, setTypes] = useState<LookupItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [searchQuery, setSearchQuery] = useState(String(storedState?.searchQuery || ''));
  const [typeFilter, setTypeFilter] = useState(String(storedState?.typeFilter || ''));
  const [page, setPage] = useState(Math.max(Number(storedState?.page) || 1, 1));
  const [pageSize, setPageSize] = useState(() => {
    const candidate = Number(storedState?.pageSize);
    return [12, 24, 48].includes(candidate) ? candidate : 12;
  });
  const [totalItems, setTotalItems] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [usesServerPagination, setUsesServerPagination] = useState(false);

  useEffect(() => {
    if (!creator) {
      navigate('/login');
      return;
    }
    
    // Redirect to profile if not complete
    if (!isProfileComplete) {
      navigate('/profile', { 
        state: { message: t('home.profileIncomplete') } 
      });
      return;
    }
    
    loadXperiences();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [creator, isProfileComplete, navigate, page, pageSize]);

  useEffect(() => {
    sessionStorage.setItem(
      HOME_DASHBOARD_STATE_KEY,
      JSON.stringify({
        searchQuery,
        typeFilter,
        page,
        pageSize
      })
    );
  }, [searchQuery, typeFilter, page, pageSize]);

  useEffect(() => {
    setPage(1);
  }, [searchQuery, typeFilter]);

  const loadXperiences = async () => {
    try {
      const [response, typesResponse] = await Promise.all([
        getXperiences({ page, limit: pageSize }),
        getXperienceTypes()
      ]);
      setXperiences(response.data.xperiences);

      const hasPagination = !!response.data.pagination;
      setUsesServerPagination(hasPagination);

      if (hasPagination) {
        setTotalItems(response.data.pagination?.totalItems || 0);
        setTotalPages(response.data.pagination?.totalPages || 1);
      } else {
        const localTotal = response.data.count || response.data.xperiences.length;
        setTotalItems(localTotal);
        setTotalPages(Math.max(Math.ceil(localTotal / pageSize), 1));
      }

      setTypes(typesResponse.data.types);
    } catch (err: any) {
      setError(t('home.error.load'));
    } finally {
      setLoading(false);
    }
  };

  const filteredXperiences = useMemo(() => {
    return xperiences.filter(x => {
      let matchesSearch = true;
      if (searchQuery.trim()) {
        const lowerQuery = searchQuery.toLowerCase();
        const titleMatch = x.title?.toLowerCase().includes(lowerQuery);
        const descMatch = x.description?.toLowerCase().includes(lowerQuery);
        matchesSearch = !!(titleMatch || descMatch);
      }

      let matchesType = true;
      if (typeFilter) {
        const typeNum = parseInt(typeFilter, 10);
        matchesType = x.mediaLinks ? x.mediaLinks.some(m => m.type === typeNum) : false;
      }

      return matchesSearch && matchesType;
    });
  }, [xperiences, searchQuery, typeFilter]);

  const pagedXperiences = useMemo(() => {
    if (usesServerPagination) {
      return filteredXperiences;
    }

    const start = (page - 1) * pageSize;
    const end = start + pageSize;
    return filteredXperiences.slice(start, end);
  }, [filteredXperiences, page, pageSize, usesServerPagination]);

  if (!creator) return null;

  return (
    <div className="home-page">
      <Navbar />
      
      <div className="home-container">
        <div className="home-header">
          <div>
            <h1 className="home-title">{t('home.title')}</h1>
            <p className="home-subtitle">{t('home.subtitle')}</p>
          </div>
          <Link to="/create-xperience">
            <Button variant="primary">{t('home.newXperience')}</Button>
          </Link>
        </div>

        {error && <div className="home-error">{error}</div>}

        {!loading && xperiences.length > 0 && (
          <div className="home-filters" style={{ marginBottom: '24px', display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
            <div style={{ flex: 1, minWidth: '250px', position: 'relative' }}>
              <input
                type="text"
                placeholder={t('home.searchPlaceholder')}
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                style={{
                  width: '100%',
                  padding: '10px 36px 10px 16px', // Right padding to fit the clear button
                  borderRadius: '8px',
                  border: '1px solid var(--border-color)',
                  backgroundColor: 'var(--bg-lighter)',
                  color: 'var(--text-primary)',
                  fontSize: '14px',
                  boxSizing: 'border-box'
                }}
              />
              {searchQuery && (
                <button
                  onClick={() => setSearchQuery('')}
                  style={{
                    position: 'absolute',
                    right: '12px',
                    top: '50%',
                    transform: 'translateY(-50%)',
                    background: 'none',
                    border: 'none',
                    color: 'var(--text-secondary)',
                    cursor: 'pointer',
                    fontSize: '16px',
                    padding: '0',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center'
                  }}
                  title={t('common.clearSearch')}
                >
                  ✕
                </button>
              )}
            </div>
            <select
              value={typeFilter}
              onChange={(e) => setTypeFilter(e.target.value)}
              style={{
                minWidth: '200px',
                padding: '10px 16px',
                borderRadius: '8px',
                border: '1px solid var(--border-color)',
                backgroundColor: 'var(--bg-lighter)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              <option value="">{t('home.allMediaTypes')}</option>
              {types.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="home-loading">{t('home.loading')}</div>
        ) : xperiences.length === 0 ? (
          <div className="home-empty">
            <div className="empty-icon">📱</div>
            <h2 className="empty-title">{t('home.emptyTitle')}</h2>
            <p className="empty-text">{t('home.emptyText')}</p>
            <Link to="/create-xperience">
              <Button variant="secondary">{t('home.createFirst')}</Button>
            </Link>
          </div>
        ) : filteredXperiences.length === 0 ? (
          <div className="home-empty">
            <h2 className="empty-title">{t('home.noResultsTitle')}</h2>
            <p className="empty-text">{t('home.noResultsText')}</p>
            <Button variant="secondary" onClick={() => { setSearchQuery(''); setTypeFilter(''); }}>{t('home.clearFilters')}</Button>
          </div>
        ) : (
          <>
            <div className="experiences-grid">
              {pagedXperiences.map((experience) => {
              // Get thumbnail from first mediaLink's posterUrl
              const firstMediaLink = experience.mediaLinks?.[0];
              const thumbnailUrl = firstMediaLink?.posterUrl 
                ? `https://x-perienc.es/${firstMediaLink.posterUrl}`
                : null;
              const visibleTags = experience.tags?.slice(0, 6) || [];
              const hiddenTagsCount = (experience.tags?.length || 0) - visibleTags.length;
              const tagOverlay = visibleTags.length > 0 && (
                <div
                  style={{
                    position: 'absolute',
                    left: '10px',
                    right: '10px',
                    bottom: '10px',
                    display: 'flex',
                    flexWrap: 'wrap',
                    justifyContent: 'flex-start',
                    gap: '6px',
                    zIndex: 2
                  }}
                >
                  {visibleTags.map((tag) => (
                    <span
                      key={`${experience.id}-tag-${tag.id}`}
                      style={{
                        background: 'rgba(0,0,0,0.45)',
                        border: '1px solid rgba(255,255,255,0.22)',
                        color: '#f1f1f1',
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        padding: '5px 8px',
                        borderRadius: '999px',
                        backdropFilter: 'blur(2px)'
                      }}
                    >
                      {tag.tag}
                    </span>
                  ))}
                  {hiddenTagsCount > 0 && (
                    <span
                      title={`${hiddenTagsCount} ${t('home.moreTags')}`}
                      style={{
                        background: 'rgba(0,0,0,0.35)',
                        border: '1px dashed rgba(255,255,255,0.28)',
                        color: '#e0e0e0',
                        fontSize: '0.7rem',
                        lineHeight: 1,
                        padding: '5px 8px',
                        borderRadius: '999px',
                        backdropFilter: 'blur(2px)'
                      }}
                    >
                      +{hiddenTagsCount}
                    </span>
                  )}
                </div>
              );
              
                return (
                  <div key={experience.id} className="experience-card">
                  {thumbnailUrl ? (
                    <div 
                      className="experience-thumbnail"
                      style={{ backgroundImage: `url(${thumbnailUrl})`, position: 'relative' }}
                    >
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: experience.status === 'approved' ? 'rgba(76, 175, 80, 0.9)' : 
                                        experience.status === 'rejected' ? 'rgba(244, 67, 54, 0.9)' : 'rgba(255, 152, 0, 0.9)',
                        color: '#fff',
                        textTransform: 'capitalize',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 2
                      }}>
                        {experience.status || 'Pending'}
                      </div>

                      {experience.mediaLinks && experience.mediaLinks.length > 0 && (
                        <div className="type-badge">
                          {experience.mediaLinks[0].type === 1 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7"></polygon>
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                          )}
                          {experience.mediaLinks[0].type === 2 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          )}
                          {experience.mediaLinks[0].type === 3 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 18V5l12-2v13"></path>
                              <circle cx="6" cy="18" r="3"></circle>
                              <circle cx="18" cy="16" r="3"></circle>
                            </svg>
                          )}
                        </div>
                      )}

                      {tagOverlay}
                    </div>
                  ) : (
                    <div className="experience-thumbnail experience-thumbnail-empty" style={{ position: 'relative' }}>
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        backgroundColor: experience.status === 'approved' ? 'rgba(76, 175, 80, 0.9)' : 
                                        experience.status === 'rejected' ? 'rgba(244, 67, 54, 0.9)' : 'rgba(255, 152, 0, 0.9)',
                        color: '#fff',
                        textTransform: 'capitalize',
                        fontSize: '0.75rem',
                        fontWeight: 'bold',
                        boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                        zIndex: 2
                      }}>
                        {experience.status || 'Pending'}
                      </div>

                      <span className="thumbnail-placeholder">📷</span>
                      {experience.mediaLinks && experience.mediaLinks.length > 0 && (
                        <div className="type-badge">
                          {experience.mediaLinks[0].type === 1 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <polygon points="23 7 16 12 23 17 23 7"></polygon>
                              <rect x="1" y="5" width="15" height="14" rx="2" ry="2"></rect>
                            </svg>
                          )}
                          {experience.mediaLinks[0].type === 2 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <rect x="3" y="3" width="18" height="18" rx="2" ry="2"></rect>
                              <circle cx="8.5" cy="8.5" r="1.5"></circle>
                              <polyline points="21 15 16 10 5 21"></polyline>
                            </svg>
                          )}
                          {experience.mediaLinks[0].type === 3 && (
                            <svg className="type-icon" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                              <path d="M9 18V5l12-2v13"></path>
                              <circle cx="6" cy="18" r="3"></circle>
                              <circle cx="18" cy="16" r="3"></circle>
                            </svg>
                          )}
                        </div>
                      )}

                      {tagOverlay}
                    </div>
                  )}
                  
                  <div className="experience-content">
                    <h3 className="experience-title" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      {experience.title}
                      {experience.isPublic === 0 && <span title={t('home.privateTitle')} style={{ fontSize: '16px' }}>🔒</span>}
                    </h3>
                    
                    {experience.description && (
                      <p className="experience-description">{experience.description}</p>
                    )}
                    
                    {/* Stats Row */}
                    <div className="experience-stats" style={{ 
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-start',
                        gap: '8px',
                        marginTop: 'auto',
                        paddingTop: '12px',
                        fontSize: '0.9rem',
                        color: '#e0e0e0'
                      }}>
                        {experience.category ? (
                          <span title={t('home.categoryTitle')} style={{ 
                              background: 'rgba(255,255,255,0.1)', 
                              padding: '2px 8px', 
                              borderRadius: '4px',
                              fontSize: '0.75rem',
                              marginRight: '8px',
                              alignSelf: 'center'
                            }}>
                              {experience.category}
                            </span>
                        ) : null}
                        
                        <div title={t('home.viewsTitle')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '30px' }}>
                          <span style={{ fontSize: '1.1rem' }}>👁️</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{experience.views || 0}</span>
                        </div>
                        
                        <div title={t('home.likesTitle')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '30px' }}>
                          <span style={{ fontSize: '1.1rem' }}>👍</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{experience.likes_count || 0}</span>
                        </div>
                        
                        <div title={t('home.dislikesTitle')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '30px' }}>
                          <span style={{ fontSize: '1.1rem' }}>👎</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{experience.dislikes_count || 0}</span>
                        </div>
                        
                        <div title={t('home.ratingTitle')} style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '2px', minWidth: '30px' }}>
                          <span style={{ fontSize: '1.1rem' }}>⭐</span>
                          <span style={{ fontSize: '0.75rem', fontWeight: 'bold' }}>{experience.rating ? Number(experience.rating).toFixed(1) : '0.0'}</span>
                        </div>
                    </div>

                    <div className="experience-footer" style={{ 
                      display: 'flex', 
                      justifyContent: 'space-between', 
                      alignItems: 'center',
                      marginTop: '16px',
                      paddingTop: '16px', 
                      borderTop: '1px solid rgba(255,255,255,0.1)' 
                    }}>
                      <Link to={`/xperience/${experience.id}`}>
                        <Button variant="ghost">{t('home.viewDetails')}</Button>
                      </Link>

                      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', fontSize: '0.75rem', color: '#888', gap: '2px' }}>
                          {experience.createdDate && (
                            <span>📅 {new Date(experience.createdDate).toLocaleDateString(undefined, { year: 'numeric', month: 'short', day: 'numeric' })}</span>
                          )}
                          {experience.mediaLinks && experience.mediaLinks.length > 0 && (
                            <span>
                              💾 {experience.mediaLinks.length}{' '}
                              {experience.mediaLinks.length !== 1 ? t('home.filesPlural') : t('home.files')}
                            </span>
                          )}
                      </div>
                    </div>
                  </div>
                  </div>
                );
              })}
            </div>

            <div style={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              flexWrap: 'wrap',
              gap: '12px',
              marginTop: '24px'
            }}>
              <div style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>
                {t('adminCreators.pagination.total')}: <strong>{usesServerPagination ? totalItems : filteredXperiences.length}</strong> | {t('adminCreators.pagination.page')} <strong>{page}</strong> {t('adminCreators.pagination.of')} <strong>{usesServerPagination ? totalPages : Math.max(Math.ceil(filteredXperiences.length / pageSize), 1)}</strong>
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', flexWrap: 'wrap' }}>
                <span style={{ color: 'var(--text-secondary)', fontSize: '0.9rem' }}>{t('adminCreators.pagination.rowsPerPage')}:</span>
                <select
                  value={pageSize}
                  onChange={(e) => {
                    setPageSize(Number(e.target.value));
                    setPage(1);
                  }}
                  style={{
                    minWidth: '86px',
                    padding: '8px 10px',
                    borderRadius: '8px',
                    border: '1px solid var(--border-color)',
                    backgroundColor: 'var(--bg-card)',
                    color: 'var(--text-primary)',
                    fontSize: '14px'
                  }}
                >
                  <option value={12}>12</option>
                  <option value={24}>24</option>
                  <option value={48}>48</option>
                </select>

                <Button variant="ghost" onClick={() => setPage(prev => Math.max(prev - 1, 1))} disabled={page <= 1}>
                  {t('adminCreators.pagination.previous')}
                </Button>
                <Button
                  variant="ghost"
                  onClick={() => setPage(prev => Math.min(prev + 1, usesServerPagination ? totalPages : Math.max(Math.ceil(filteredXperiences.length / pageSize), 1)))}
                  disabled={page >= (usesServerPagination ? totalPages : Math.max(Math.ceil(filteredXperiences.length / pageSize), 1))}
                >
                  {t('adminCreators.pagination.next')}
                </Button>
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};

export default HomePage;
